/**
 * Theme Context for PACS Application
 * Manages light/dark mode with auto-detection for imaging pages
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Routes that should default to dark mode (imaging workflows)
// Disabled auto-dark mode due to contrast issues - users can toggle manually
const DARK_MODE_ROUTES = [
  // '/image-report',  // Disabled - contrast issues
  // '/viewer',
  // '/admin/image-report',
  // '/radiologist/image-report',
  // '/juniorRadiologist/image-report',
  // '/seniorRadiologist/image-report',
];

// Storage keys
const THEME_STORAGE_KEY = 'pacs-theme-mode';
const IMAGING_THEME_STORAGE_KEY = 'pacs-imaging-theme-mode';

// Check if current path should use dark mode by default
const isImagingRoute = (pathname) => {
  return DARK_MODE_ROUTES.some(route => pathname.includes(route));
};

// Theme Context
const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
  setMode: () => {},
  isImaging: false,
});

// Custom hook to use theme context
export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

// Theme Context Provider
export const ThemeContextProvider = ({ children }) => {
  const location = useLocation();
  const isImaging = isImagingRoute(location.pathname);

  // TEMPORARILY DISABLED: Dark mode toggle
  // Always use light mode until dark mode styling is fixed
  const mode = 'light';

  // Disabled: Initialize mode based on stored preference or route
  // const [mode, setModeState] = useState(() => {
  //   // Check if we're on an imaging route
  //   if (typeof window !== 'undefined') {
  //     const currentIsImaging = isImagingRoute(window.location.pathname);
  //
  //     if (currentIsImaging) {
  //       // For imaging routes, use imaging-specific preference or default to dark
  //       const storedImagingMode = localStorage.getItem(IMAGING_THEME_STORAGE_KEY);
  //       return storedImagingMode || 'dark';
  //     } else {
  //       // For other routes, use general preference or default to light
  //       const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
  //       return storedMode || 'light';
  //     }
  //   }
  //   return 'light';
  // });

  // Disabled: Update mode when route changes
  // useEffect(() => {
  //   if (isImaging) {
  //     // Switching to imaging route
  //     const storedImagingMode = localStorage.getItem(IMAGING_THEME_STORAGE_KEY);
  //     setModeState(storedImagingMode || 'dark');
  //   } else {
  //     // Switching to non-imaging route
  //     const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
  //     setModeState(storedMode || 'light');
  //   }
  // }, [isImaging]);

  // Disabled: Set mode and persist
  const setMode = useCallback(() => {
    // Dark mode temporarily disabled - do nothing
  }, []);

  // Disabled: Toggle between light and dark
  const toggleTheme = useCallback(() => {
    // Dark mode temporarily disabled - do nothing
  }, []);

  // Memoize context value
  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
    setMode,
    isImaging,
  }), [mode, toggleTheme, setMode, isImaging]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
